import { t } from '../i18n';

export interface CaptureReviewResult {
  /** 0 = not rated. */
  rating: number;
  notes: string;
}

/**
 * Post-capture review sheet (visitor): shown right after the arrival
 * auto-capture finishes. Photo + video preview side by side, 1–5 star
 * "Rate this spot", free-text notes, Save/Skip. The result is uploaded as a
 * notes JSON next to the media (captureService.submitCaptureNote) and folded
 * into the Dashboard's PDF report. Skip closes without saving — media itself
 * is already uploaded either way.
 */
export class CaptureReviewSheet {
  readonly el: HTMLDivElement;
  private onDone: ((result: CaptureReviewResult | null) => void) | null = null;
  private rating = 0;
  private starBtns: HTMLButtonElement[] = [];
  private notesEl: HTMLTextAreaElement;
  private titleEl: HTMLHeadingElement;
  private chipEl: HTMLSpanElement;
  private photoEl: HTMLImageElement;
  private videoEl: HTMLVideoElement;

  constructor() {
    this.el = document.createElement('div');
    this.el.className = 'sheet cap-review hidden';
    this.el.innerHTML = `
      <div class="sheet-handle"></div>
      <span class="cap-badge"></span>
      <h2 data-role="title"></h2>
      <span class="cap-chip" data-role="chip"></span>
      <div class="cap-previews">
        <figure><img data-role="photo" alt="" /><figcaption></figcaption></figure>
        <figure><video data-role="video" controls playsinline muted></video><figcaption></figcaption></figure>
      </div>
      <p class="cap-rate-label" data-role="rate-label"></p>
      <div class="cap-stars" data-role="stars"></div>
      <label class="cap-notes-label"><span data-role="notes-label"></span>
        <textarea data-role="notes" rows="3"></textarea>
      </label>
      <div class="admin-buttons">
        <button class="btn ghost" data-role="skip"></button>
        <button class="btn primary" data-role="save"></button>
      </div>
    `;
    this.titleEl = this.el.querySelector('[data-role="title"]')!;
    this.chipEl = this.el.querySelector('[data-role="chip"]')!;
    this.photoEl = this.el.querySelector('[data-role="photo"]')!;
    this.videoEl = this.el.querySelector('[data-role="video"]')!;
    this.notesEl = this.el.querySelector('[data-role="notes"]')!;

    const stars = this.el.querySelector('[data-role="stars"]')!;
    for (let i = 1; i <= 5; i++) {
      const b = document.createElement('button');
      b.className = 'cap-star';
      b.type = 'button';
      b.textContent = '☆';
      b.setAttribute('aria-label', `${i} / 5`);
      b.addEventListener('click', () => this.setRating(this.rating === i ? 0 : i));
      stars.appendChild(b);
      this.starBtns.push(b);
    }

    (this.el.querySelector('[data-role="skip"]') as HTMLButtonElement).addEventListener('click', () =>
      this.close(null),
    );
    (this.el.querySelector('[data-role="save"]') as HTMLButtonElement).addEventListener('click', () =>
      this.close({ rating: this.rating, notes: this.notesEl.value.trim() }),
    );
  }

  show(
    opts: { poiName: string; photoUrl: string; videoUrl: string; seconds: number },
    onDone: (result: CaptureReviewResult | null) => void,
  ): void {
    this.onDone = onDone;
    // Text resolves at show time (visitor language may have changed).
    (this.el.querySelector('.cap-badge') as HTMLElement).textContent = t('cap.reviewBadge');
    this.titleEl.textContent = t('cap.reviewTitle', { name: opts.poiName });
    this.chipEl.textContent = t('cap.recordingChip', { s: opts.seconds });
    (this.el.querySelector('[data-role="rate-label"]') as HTMLElement).textContent = t('cap.rate');
    (this.el.querySelector('[data-role="notes-label"]') as HTMLElement).textContent = t('cap.notes');
    this.notesEl.placeholder = t('cap.notesPh');
    (this.el.querySelector('[data-role="skip"]') as HTMLButtonElement).textContent = t('cap.skip');
    (this.el.querySelector('[data-role="save"]') as HTMLButtonElement).textContent = t('cap.save');
    const caps = this.el.querySelectorAll('figcaption');
    caps[0].textContent = t('cap.snapshot');
    caps[1].textContent = t('cap.video');

    this.photoEl.src = opts.photoUrl;
    this.videoEl.src = opts.videoUrl;
    this.setRating(0);
    this.notesEl.value = '';
    this.el.classList.remove('hidden');
  }

  private setRating(n: number): void {
    this.rating = n;
    this.starBtns.forEach((b, i) => {
      b.textContent = i < n ? '★' : '☆';
      b.classList.toggle('on', i < n);
    });
  }

  private close(result: CaptureReviewResult | null): void {
    this.el.classList.add('hidden');
    this.videoEl.pause();
    this.videoEl.removeAttribute('src');
    this.videoEl.load();
    const cb = this.onDone;
    this.onDone = null;
    cb?.(result);
  }
}
